import { useState, useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/button'
import { Modal } from 'src/components/modal'
import { expenseSearchApi } from 'src/services/expense-searches/api'
import { searchTypeEnum } from 'src/services/expense-searches/models'
import { taskTypeEnum } from 'src/services/async-jobs/models'
import { useRecentJob } from 'src/stores/async-jobs'
import { cn } from 'src/utils/style'
import { FunnelIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { Spinner } from 'src/components/spinner'

interface AISearchModalProps {
  isOpen: boolean
  onClose: () => void
  onSearchComplete?: (filters: Record<string, unknown>, uiFilters: Record<string, unknown>) => void
  /** Whether the semantic (vector) search mode is offered. Defaults to true. */
  vectorEnabled?: boolean
  /** Whether the query builder search mode is offered. Defaults to true. */
  queryBuilderEnabled?: boolean
}

export const AISearchModal = ({
  isOpen,
  onClose,
  onSearchComplete,
  vectorEnabled = true,
  queryBuilderEnabled = true,
}: AISearchModalProps) => {
  const { t } = useTranslation()
  // Show the mode switcher only when both modes are available; with a single
  // mode enabled it is preselected and the switcher is hidden.
  const showModeSwitcher = vectorEnabled && queryBuilderEnabled
  const [searchType, setSearchType] = useState<'vector' | 'query_builder'>(
    queryBuilderEnabled ? 'query_builder' : 'vector',
  )

  // Keep the selected mode pointed at an enabled one when the flags change.
  useEffect(() => {
    if (searchType === 'vector' && !vectorEnabled && queryBuilderEnabled) {
      setSearchType('query_builder')
    } else if (searchType === 'query_builder' && !queryBuilderEnabled && vectorEnabled) {
      setSearchType('vector')
    }
  }, [vectorEnabled, queryBuilderEnabled, searchType])
  const [query, setQuery] = useState('')
  const [searchId, setSearchId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const taskType =
    searchType === 'query_builder'
      ? taskTypeEnum.GENERATE_FILTERS
      : taskTypeEnum.GENERATE_QUERY_VECTOR

  const recentJob = useRecentJob(taskType, searchId ?? '')

  // Refs to hold callback references stable for the completion effect
  const onSearchCompleteRef = useRef(onSearchComplete)
  onSearchCompleteRef.current = onSearchComplete
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const createSearchMutation = useMutation({
    mutationFn: async () => {
      return await expenseSearchApi.create({
        query,
        searchType: searchType === 'vector' ? searchTypeEnum.VECTOR : searchTypeEnum.QUERY_BUILDER,
      })
    },
    onSuccess: (data) => {
      setSearchId(data.id)
      setError(null)
    },
    onError: () => {
      setError(t('aiSearch.failedToCreate'))
    },
  })

  // Detect job completion — re-fetch search for populated filters
  useEffect(() => {
    if (!searchId || !recentJob) return

    if (recentJob.status === 'done') {
      expenseSearchApi.retrieve(searchId).then((search) => {
        onSearchCompleteRef.current?.(search.apiFilters ?? {}, search.uiFilters ?? {})
        setQuery('')
        setSearchId(null)
        onCloseRef.current()
      })
    } else if (recentJob.status === 'failed') {
      setError(recentJob.errorMessage || t('aiSearch.searchFailed'))
      setSearchId(null)
    }
  }, [searchId, recentJob, t])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchId(null)
      setError(null)
    }
  }, [isOpen])

  const isSearching = createSearchMutation.isPending || Boolean(searchId)

  const handleSearch = () => {
    if (!query.trim()) return
    setError(null)
    createSearchMutation.mutate()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSearch()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('aiSearch.modalTitle')}>
      <div className="space-y-4">
        {/* Search Type Toggle — only shown when both modes are available */}
        {showModeSwitcher && (
          <div className="flex gap-2" data-testid="search-mode-switcher">
            <button
              type="button"
              onClick={() => setSearchType('query_builder')}
              disabled={isSearching}
              className={cn([
                'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 transition-all',
                searchType === 'query_builder'
                  ? 'border-primary bg-primary-50 text-primary'
                  : 'border-border text-text-secondary hover:border-border-strong',
                isSearching && 'cursor-not-allowed opacity-50',
              ])}
            >
              <FunnelIcon className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">{t('aiSearch.queryBuilder')}</div>
                <div className="text-xs opacity-75">{t('aiSearch.queryBuilderHint')}</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setSearchType('vector')}
              disabled={isSearching}
              className={cn([
                'flex flex-1 items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 transition-all',
                searchType === 'vector'
                  ? 'bg-accent/10 border-accent text-accent'
                  : 'border-border text-text-secondary hover:border-border-strong',
                isSearching && 'cursor-not-allowed opacity-50',
              ])}
            >
              <SparklesIcon className="h-5 w-5" />
              <div className="text-left">
                <div className="font-medium">{t('aiSearch.semanticSearch')}</div>
                <div className="text-xs opacity-75">{t('aiSearch.semanticSearchHint')}</div>
              </div>
            </button>
          </div>
        )}

        {/* Loading state while job is running */}
        {isSearching && !createSearchMutation.isPending && (
          <div
            className="flex items-center gap-3 rounded-lg border border-border bg-surface p-4"
            data-testid="search-generating"
          >
            <Spinner size="sm" />
            <div>
              <p className="text-sm font-medium text-primary-700">
                {searchType === 'query_builder'
                  ? t('aiSearch.generatingFilters')
                  : t('aiSearch.generatingEmbedding')}
              </p>
              <p className="text-xs text-primary-500">{t('aiSearch.waitMessage')}</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div
            className="rounded-lg bg-error-50 p-3 text-sm text-error-700"
            data-testid="search-modal-error"
          >
            {error}
          </div>
        )}

        {/* Search Input */}
        {!isSearching && (
          <div>
            <label
              htmlFor="ai-search-query"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t('aiSearch.inputLabel')}
            </label>
            <textarea
              id="ai-search-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                searchType === 'query_builder'
                  ? 'e.g., "Show me travel expenses over $500 from last quarter"'
                  : 'e.g., "Office supplies and equipment purchases"'
              }
              rows={3}
              className="focus:ring-primary/20 w-full resize-none rounded-lg border border-border-strong p-3 text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none focus:ring-2"
            />
            <p className="mt-1 text-xs text-text-placeholder">
              {searchType === 'query_builder'
                ? t('aiSearch.queryBuilderInputHint')
                : t('aiSearch.semanticInputHint')}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={isSearching}>
            {t('common.cancel')}
          </Button>
          {!isSearching && (
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={!query.trim() || createSearchMutation.isPending}
            >
              <span className="flex items-center gap-2">
                {searchType === 'query_builder' ? (
                  <FunnelIcon className="h-4 w-4" />
                ) : (
                  <SparklesIcon className="h-4 w-4" />
                )}
                {t('common.search')}
              </span>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
